import { useCallback, useEffect, useState } from 'react';
import {
  FileText,
  Building2,
  CreditCard,
  ShieldAlert,
  CalendarClock,
  BellRing,
  Download,
  Undo2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Alert } from '../components/Alert';
import { Input } from '../components/Input';
import { Loader } from '../components/Loader';
import { Dialog } from '../components/Dialog';
import { EmbeddedPayment } from '../components/EmbeddedPayment';
import { SubscriptionsCard } from '../components/SubscriptionChanges';
import { api } from '../services/api';
import {
  BillingAccount,
  BillingAccountUpdate,
  InvoiceDetail,
  InvoicePayResult,
  InvoiceStatus,
  InvoiceSummary,
  CreditNoteSummary,
  BillingSubscription,
  PaymentMethodSummary,
  UpcomingInvoice,
} from '../types';

const BILLING_ROLES = ['owner', 'admin', 'billing'];
const CANCEL_ROLES = ['owner', 'billing'];

const INVOICE_STATUS: Record<InvoiceStatus, { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  open: { label: 'Due', tone: 'warning' },
  paid: { label: 'Paid', tone: 'success' },
  uncollectible: { label: 'Unpaid', tone: 'danger' },
  void: { label: 'Cancelled', tone: 'neutral' },
};

function money(minor: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: minor % 100 === 0 ? 0 : 2,
  }).format(minor / 100);
}

function day(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        borderBottom: '1px solid var(--border-subtle)',
        paddingBottom: 8,
      }}
    >
      <span style={{ color: 'var(--text-tertiary)' }}>{label}</span>
      <span style={{ fontWeight: 500, textAlign: 'right' }}>{children}</span>
    </div>
  );
}

function SectionTitle({ icon, title, extra }: { icon: React.ReactNode; title: string; extra?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {icon}
        <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)' }}>{title}</h2>
      </div>
      {extra}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 'var(--space-6)',
        background: 'var(--bg-inset)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-subtle)',
        textAlign: 'center',
        fontSize: 'var(--text-sm)',
        color: 'var(--text-tertiary)',
      }}
    >
      {children}
    </div>
  );
}

function PaymentMethodsCard({ methods }: { methods: PaymentMethodSummary[] }) {
  return (
    <Card padding="var(--space-6)">
      <SectionTitle icon={<CreditCard size={20} color="var(--accent)" />} title="Payment method" />
      {methods.length === 0 ? (
        <Empty>No saved payment method. The card you pay with at checkout is kept for renewals.</Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 'var(--text-sm)' }}>
          {methods.map((m) => (
            <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>
                {m.type === 'card' ? `${m.brand || 'Card'} •••• ${m.last4 || '????'}` : m.type.replace('_', ' ')}
              </span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {m.exp_month && m.exp_year && (
                  <span style={{ color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
                    {String(m.exp_month).padStart(2, '0')}/{String(m.exp_year).slice(-2)}
                  </span>
                )}
                {m.is_default && <Badge tone="accent">Default</Badge>}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function UpcomingInvoicesCard({
  upcoming,
  canCancel,
  onCancel,
  onReinstate,
}: {
  upcoming: UpcomingInvoice[];
  canCancel: boolean;
  onCancel: (u: UpcomingInvoice) => void;
  onReinstate: (u: UpcomingInvoice) => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  return (
    <Card padding="var(--space-6)">
      <SectionTitle icon={<CalendarClock size={20} color="var(--accent)" />} title="Upcoming invoices" />
      {upcoming.length === 0 ? (
        <Empty>Nothing scheduled. Paid plans show their next invoice here.</Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {upcoming.map((u) => (
            <div
              key={u.subscription_id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
                padding: '12px 14px',
                background: 'var(--bg-inset)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-subtle)',
                fontSize: 'var(--text-sm)',
                opacity: u.cancelled ? 0.85 : 1,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span
                    style={{
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      textDecoration: u.cancelled ? 'line-through' : undefined,
                    }}
                  >
                    {money(u.amount_minor, u.currency)} on {day(u.issue_on)}
                  </span>
                  {u.cancelled ? <Badge tone="danger">Cancelled</Badge> : <Badge tone="neutral">Scheduled</Badge>}
                </div>
                <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', marginTop: 2 }}>
                  {u.description} · {day(u.period_start)} – {day(u.period_end)}
                </div>
                {u.cancelled && (
                  <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', marginTop: 4 }}>
                    Will not be issued. Your plan stays active until {day(u.issue_on)}, then stops.
                  </div>
                )}
              </div>
              {canCancel &&
                (u.cancelled ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy === u.subscription_id}
                    onClick={async () => {
                      setBusy(u.subscription_id);
                      try {
                        await onReinstate(u);
                      } finally {
                        setBusy(null);
                      }
                    }}
                  >
                    Reinstate
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => onCancel(u)}>
                    Cancel invoice
                  </Button>
                ))}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function CancelDialog({
  invoice,
  onClose,
  onConfirm,
}: {
  invoice: UpcomingInvoice | null;
  onClose: () => void;
  onConfirm: (u: UpcomingInvoice) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!invoice) return null;
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(invoice);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel the invoice');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      title="Cancel this invoice?"
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Keep it
          </Button>
          <Button variant="danger" size="sm" onClick={confirm} disabled={busy}>
            {busy ? 'Cancelling...' : 'Cancel invoice'}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 'var(--text-sm)', lineHeight: 1.6 }}>
        {error && (
          <Alert tone="danger" title="Error">
            {error}
          </Alert>
        )}
        <p>
          The {money(invoice.amount_minor, invoice.currency)} invoice for <strong>{invoice.description}</strong> due on{' '}
          {day(invoice.issue_on)} will not be issued, and nothing more will be charged for this plan.
        </p>
        <p style={{ color: 'var(--text-secondary)' }}>
          This ends the subscription: it stays active until {day(invoice.issue_on)}, the period you have already paid
          for, then stops. That period is not refunded. You can reinstate the invoice until then.
        </p>
      </div>
    </Dialog>
  );
}

function PayDialog({
  orgId,
  invoice,
  onClose,
  onPaid,
}: {
  orgId: string;
  invoice: InvoiceSummary | null;
  onClose: () => void;
  onPaid: () => Promise<void>;
}) {
  const [start, setStart] = useState<InvoicePayResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    setStart(null);
    setError(null);
    setWaiting(false);
    if (!invoice) return;
    api.billing
      .payInvoice(orgId, invoice.id)
      .then(async (r) => {
        if (r.status === 'paid') {
          await onPaid();
          onClose();
        } else setStart(r);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not open the payment form'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, invoice?.id]);

  const complete = async () => {
    if (!invoice) return;
    setWaiting(true);
    // Stripe confirms to Nexus, not to the page: ask until it has.
    for (let i = 0; i < 15; i++) {
      try {
        const r = await api.billing.refreshInvoice(orgId, invoice.id);
        if (r.status === 'paid') break;
      } catch {
        // keep asking
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
    await onPaid();
    onClose();
  };

  if (!invoice) return null;
  return (
    <Dialog open title={`Pay invoice ${invoice.number}`} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 'var(--text-sm)' }}>
        <p style={{ color: 'var(--text-secondary)' }}>
          {money(invoice.amount_due_minor, invoice.currency)} due. The card you pay with becomes the one used for
          future renewals.
        </p>
        {error && (
          <Alert tone="danger" title="Error">
            {error}
          </Alert>
        )}
        {waiting ? (
          <Loader label="Payment received, confirming..." />
        ) : (
          start?.client_secret &&
          start.publishable_key && (
            <EmbeddedPayment
              publishableKey={start.publishable_key}
              clientSecret={start.client_secret}
              onComplete={complete}
              onError={() => setError('The payment form could not be loaded.')}
            />
          )
        )}
        {!start && !error && <Loader label="Opening the payment form..." />}
      </div>
    </Dialog>
  );
}

function OverdueAlert({
  invoices,
  canPay,
  onPay,
}: {
  invoices: InvoiceSummary[];
  canPay: boolean;
  onPay: (inv: InvoiceSummary) => void;
}) {
  if (invoices.length === 0) return null;
  return (
    <Alert tone="warning" title={invoices.length === 1 ? 'An invoice is unpaid' : `${invoices.length} invoices are unpaid`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {invoices.map((inv) => (
          <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>
              <strong>{inv.number}</strong> · {money(inv.amount_due_minor, inv.currency)} ·{' '}
              {inv.next_payment_attempt_at
                ? `the saved card will be charged again on ${day(inv.next_payment_attempt_at)}`
                : 'no more automatic attempts; pay it to keep your plan running'}
            </span>
            {canPay && (
              <Button size="sm" variant="primary" onClick={() => onPay(inv)}>
                Pay now
              </Button>
            )}
          </div>
        ))}
      </div>
    </Alert>
  );
}

function RemindersCard({
  account,
  onToggle,
}: {
  account: BillingAccount;
  onToggle: (on: boolean) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Card padding="var(--space-6)">
      <SectionTitle icon={<BellRing size={20} color="var(--accent)" />} title="Renewal reminders" />
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={account.renewal_reminders}
          disabled={busy}
          onChange={async (e) => {
            setBusy(true);
            try {
              await onToggle(e.target.checked);
            } finally {
              setBusy(false);
            }
          }}
          style={{ marginTop: 3 }}
        />
        <span>
          Email me before a subscription renews
          <span style={{ display: 'block', color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', marginTop: 2 }}>
            30 days before yearly plans, 3 days before monthly ones. Invoices and payment problems are always emailed.
          </span>
        </span>
      </label>
    </Card>
  );
}

function BillingDetailsCard({
  account,
  onSave,
}: {
  account: BillingAccount;
  onSave: (update: BillingAccountUpdate) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<BillingAccountUpdate>({});
  const business = account.customer_kind === 'business';
  // While editing, the form's own choice drives the company fields.
  const editingBusiness = (form.customer_kind ?? account.customer_kind) === 'business';

  const start = () => {
    setForm({
      customer_kind: account.customer_kind === 'business' ? 'business' : 'individual',
      legal_name: account.legal_name,
      billing_email: account.billing_email,
      address_line1: account.address_line1,
      address_line2: account.address_line2,
      postal_code: account.postal_code,
      city: account.city,
      country: account.country,
      siren: account.siren,
      siret: account.siret,
      vat_number: account.vat_number,
    });
    setError(null);
    setEditing(true);
  };

  const field = (key: keyof BillingAccountUpdate) => ({
    value: (form[key] as string | null) || '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value })),
    disabled: saving,
  });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const clean: BillingAccountUpdate = {};
      for (const [k, v] of Object.entries(form)) {
        (clean as Record<string, string | boolean | null>)[k] = typeof v === 'string' ? v.trim() || null : v;
      }
      if (!clean.country) delete clean.country;
      await onSave(clean);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save billing details');
    } finally {
      setSaving(false);
    }
  };

  const address = [
    account.address_line1,
    account.address_line2,
    [account.postal_code, account.city].filter(Boolean).join(' '),
    account.country,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Card padding="var(--space-6)">
      <SectionTitle
        icon={<Building2 size={20} color="var(--accent)" />}
        title="Billing details"
        extra={
          !editing && (
            <Button size="sm" variant="secondary" onClick={start}>
              Edit
            </Button>
          )
        }
      />
      {editing ? (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {error && (
            <Alert tone="danger" title="Error">
              {error}
            </Alert>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>Customer</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['individual', 'business'] as const).map((k) => (
                <Button
                  key={k}
                  type="button"
                  size="sm"
                  variant={editingBusiness === (k === 'business') ? 'primary' : 'secondary'}
                  disabled={saving}
                  onClick={() => setForm((f) => ({ ...f, customer_kind: k }))}
                >
                  {k === 'individual' ? 'Individual' : 'Business'}
                </Button>
              ))}
            </div>
          </div>
          <Input label={editingBusiness ? 'Company name' : 'Full name'} {...field('legal_name')} />
          <Input label="Invoice email" type="email" {...field('billing_email')} />
          <Input label="Address" {...field('address_line1')} />
          <Input label="Address (line 2)" {...field('address_line2')} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 'var(--space-3)' }}>
            <Input label="Postcode" {...field('postal_code')} />
            <Input label="City" {...field('city')} />
            <Input label="Country" {...field('country')} mono />
          </div>
          {editingBusiness && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                <Input label="SIREN" {...field('siren')} mono />
                <Input label="SIRET" {...field('siret')} mono />
              </div>
              <Input label="VAT number" {...field('vat_number')} mono />
            </>
          )}
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
            Changes apply to future invoices. Invoices already issued keep the details they were issued with.
            {business && !editingBusiness
              ? ' Switching to an individual removes the SIREN, SIRET and VAT number from this account.'
              : ''}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Button variant="ghost" size="sm" type="button" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 'var(--text-sm)' }}>
          <Row label="Customer">{business ? 'Business' : 'Individual'}</Row>
          <Row label={business ? 'Company' : 'Name'}>{account.legal_name || 'Not provided'}</Row>
          <Row label="Invoice email">
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
              {account.billing_email || 'Not provided'}
            </span>
          </Row>
          <Row label="Address">{address || 'Not provided'}</Row>
          {business && (
            <>
              <Row label="SIREN / SIRET">
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
                  {account.siren || '—'} / {account.siret || '—'}
                </span>
              </Row>
              <Row label="VAT number">
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
                  {account.vat_number || '—'}
                </span>
              </Row>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function InvoiceDialog({ orgId, invoiceId, onClose }: { orgId: string; invoiceId: string | null; onClose: () => void }) {
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!invoiceId) return;
    setInvoice(null);
    setError(null);
    api.billing
      .invoice(orgId, invoiceId)
      .then(setInvoice)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load invoice'));
  }, [orgId, invoiceId]);

  if (!invoiceId) return null;
  const buyer = (invoice?.buyer_snapshot || {}) as Record<string, string | undefined>;

  return (
    <Dialog open title={invoice ? `Invoice ${invoice.number}` : 'Invoice'} onClose={onClose} maxWidth={620}>
      {error && (
        <Alert tone="danger" title="Error">
          {error}
        </Alert>
      )}
      {!invoice && !error && <Loader label="Loading invoice..." />}
      {invoice && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 'var(--text-sm)' }}>
          {!invoice.livemode && (
            <Alert tone="warning" title="Test invoice">
              Issued for a test payment. It has no accounting value and is not transmitted.
            </Alert>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Row label="Status">
              <Badge tone={INVOICE_STATUS[invoice.status].tone}>{INVOICE_STATUS[invoice.status].label}</Badge>
            </Row>
            <Row label="Issued">{day(invoice.issued_at)}</Row>
            {invoice.paid_at && <Row label="Paid">{day(invoice.paid_at)}</Row>}
            <Row label="Billed to">{buyer.legal_name || buyer.name || '—'}</Row>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', textAlign: 'left' }}>
                <th style={{ padding: '6px 0', fontWeight: 500 }}>Description</th>
                <th style={{ padding: '6px 0', fontWeight: 500, textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.lines.map((l) => (
                <tr key={l.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '8px 0' }}>
                    <div>{l.description}</div>
                    {l.period_start && l.period_end && (
                      <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
                        {day(l.period_start)} – {day(l.period_end)}
                      </div>
                    )}
                    {l.discount_minor > 0 && (
                      <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}>
                        {l.discount_label || 'Discount'}: −{money(l.discount_minor, invoice.currency)}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '8px 0', textAlign: 'right', verticalAlign: 'top' }}>
                    {money(l.total_minor, invoice.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
            <div style={{ color: 'var(--text-secondary)' }}>
              Subtotal: {money(invoice.subtotal_minor, invoice.currency)}
            </div>
            <div style={{ color: 'var(--text-secondary)' }}>VAT: {money(invoice.tax_minor, invoice.currency)}</div>
            <div style={{ fontWeight: 700, fontSize: 'var(--text-md)' }}>
              Total: {money(invoice.total_minor, invoice.currency)}
            </div>
          </div>
          {invoice.tax_note && (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{invoice.tax_note}</p>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <a href={api.billing.invoicePdfUrl(orgId, invoice.id)} download style={{ textDecoration: 'none' }}>
              <Button size="sm" variant="secondary" type="button">
                <Download size={14} /> Download PDF
              </Button>
            </a>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function InvoicesCard({
  orgId,
  invoices,
  onOpen,
}: {
  orgId: string;
  invoices: InvoiceSummary[];
  onOpen: (id: string) => void;
}) {
  return (
    <Card padding="var(--space-6)">
      <SectionTitle
        icon={<FileText size={20} color="var(--accent)" />}
        title="Invoices"
        extra={<Badge tone="neutral">{invoices.length === 1 ? '1 invoice' : `${invoices.length} invoices`}</Badge>}
      />
      {invoices.length === 0 ? (
        <Empty>No invoices yet. Each payment is invoiced and listed here.</Empty>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
          <thead>
            <tr style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', textAlign: 'left' }}>
              <th style={{ padding: '6px 0', fontWeight: 500 }}>Number</th>
              <th style={{ padding: '6px 0', fontWeight: 500 }}>Date</th>
              <th style={{ padding: '6px 0', fontWeight: 500 }}>Status</th>
              <th style={{ padding: '6px 0', fontWeight: 500, textAlign: 'right' }}>Total</th>
              <th style={{ padding: '6px 0', width: 40 }} aria-label="PDF" />
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr
                key={inv.id}
                onClick={() => onOpen(inv.id)}
                style={{ borderTop: '1px solid var(--border-subtle)', cursor: 'pointer' }}
              >
                <td style={{ padding: '10px 0', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
                  {inv.number} {!inv.livemode && <Badge tone="warning">Test</Badge>}
                </td>
                <td style={{ padding: '10px 0' }}>{day(inv.issued_at)}</td>
                <td style={{ padding: '10px 0' }}>
                  <Badge tone={INVOICE_STATUS[inv.status].tone}>{INVOICE_STATUS[inv.status].label}</Badge>
                </td>
                <td style={{ padding: '10px 0', textAlign: 'right', fontWeight: 600 }}>
                  {money(inv.total_minor, inv.currency)}
                </td>
                <td style={{ padding: '10px 0', textAlign: 'right' }}>
                  <a
                    href={api.billing.invoicePdfUrl(orgId, inv.id)}
                    download
                    onClick={(e) => e.stopPropagation()}
                    title={`Download ${inv.number} (PDF)`}
                    aria-label={`Download ${inv.number} (PDF)`}
                    style={{ color: 'var(--text-secondary)', display: 'inline-flex', padding: 4 }}
                  >
                    <Download size={16} />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function CreditNotesCard({ orgId, notes }: { orgId: string; notes: CreditNoteSummary[] }) {
  return (
    <Card padding="var(--space-6)">
      <SectionTitle
        icon={<Undo2 size={20} color="var(--accent)" />}
        title="Credit notes"
        extra={<Badge tone="neutral">{notes.length === 1 ? '1 credit note' : `${notes.length} credit notes`}</Badge>}
      />
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
        <thead>
          <tr style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', textAlign: 'left' }}>
            <th style={{ padding: '6px 0', fontWeight: 500 }}>Number</th>
            <th style={{ padding: '6px 0', fontWeight: 500 }}>Date</th>
            <th style={{ padding: '6px 0', fontWeight: 500 }}>Reason</th>
            <th style={{ padding: '6px 0', fontWeight: 500, textAlign: 'right' }}>Amount</th>
            <th style={{ padding: '6px 0', width: 40 }} aria-label="PDF" />
          </tr>
        </thead>
        <tbody>
          {notes.map((n) => (
            <tr key={n.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
              <td style={{ padding: '10px 0', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>
                {n.number} {!n.livemode && <Badge tone="warning">Test</Badge>}
              </td>
              <td style={{ padding: '10px 0' }}>{day(n.issued_at)}</td>
              <td style={{ padding: '10px 0', color: 'var(--text-secondary)' }}>{n.reason}</td>
              <td style={{ padding: '10px 0', textAlign: 'right', fontWeight: 600 }}>
                −{money(n.total_minor, n.currency)}
                {n.refunded_minor > 0 && (
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', fontWeight: 400 }}>
                    {money(n.refunded_minor, n.currency)} refunded to your card
                  </div>
                )}
              </td>
              <td style={{ padding: '10px 0', textAlign: 'right' }}>
                <a
                  href={api.billing.creditNotePdfUrl(orgId, n.id)}
                  download
                  title={`Download ${n.number} (PDF)`}
                  aria-label={`Download ${n.number} (PDF)`}
                  style={{ color: 'var(--text-secondary)', display: 'inline-flex', padding: 4 }}
                >
                  <Download size={16} />
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export function BillingPage() {
  const { activeOrg, activeRole } = useAuth();
  const [account, setAccount] = useState<BillingAccount | null>(null);
  const [methods, setMethods] = useState<PaymentMethodSummary[]>([]);
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [creditNotes, setCreditNotes] = useState<CreditNoteSummary[]>([]);
  const [subscriptions, setSubscriptions] = useState<BillingSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openInvoice, setOpenInvoice] = useState<string | null>(null);
  const [upcoming, setUpcoming] = useState<UpcomingInvoice[]>([]);
  const [cancelling, setCancelling] = useState<UpcomingInvoice | null>(null);
  const [paying, setPaying] = useState<InvoiceSummary | null>(null);

  const allowed = !!activeRole && BILLING_ROLES.includes(activeRole);

  //: Set when a product sent the customer here — `/billing/add` resolves which
  //: subscription ran out and redirects with it, having validated `return_to`
  //: against the catalogue's product origins. Read once, on mount: re-reading
  //: would reopen the dialog every time the page reloads its data.
  const [entry] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return {
      add: q.get('add'),
      returnTo: q.get('return_to'),
      //: Set when the customer followed "add more" from a product but holds
      //: nothing that can take units — every plan is comped or managed. Without
      //: this they land here with no idea why nothing opened.
      unavailable: q.get('add_unavailable'),
    };
  });

  const load = useCallback(async () => {
    if (!activeOrg?.id || !allowed) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const [acc, pm, inv, cat, up, cn] = await Promise.allSettled([
      api.billing.account(activeOrg.id),
      api.billing.paymentMethods(activeOrg.id),
      api.billing.invoices(activeOrg.id),
      api.billing.subscriptions(activeOrg.id),
      api.billing.upcoming(activeOrg.id),
      api.billing.creditNotes(activeOrg.id),
    ]);
    if (cn.status === 'fulfilled') setCreditNotes(cn.value);
    if (up.status === 'fulfilled') setUpcoming(up.value);
    if (acc.status === 'fulfilled') setAccount(acc.value);
    if (pm.status === 'fulfilled') setMethods(pm.value);
    if (inv.status === 'fulfilled') setInvoices(inv.value);
    if (cat.status === 'fulfilled') setSubscriptions(cat.value);
    const failed = [acc, pm, inv, cat, up, cn].find((r) => r.status === 'rejected') as
      | PromiseRejectedResult
      | undefined;
    setError(failed ? (failed.reason instanceof Error ? failed.reason.message : 'Failed to load billing') : null);
    setLoading(false);
  }, [activeOrg?.id, allowed]);

  useEffect(() => {
    load();
  }, [load]);

  const saveAccount = async (update: BillingAccountUpdate) => {
    if (!activeOrg?.id) return;
    setAccount(await api.billing.updateAccount(activeOrg.id, update));
    setSuccess('Billing details saved.');
  };

  const cancelInvoice = async (u: UpcomingInvoice) => {
    if (!activeOrg?.id) return;
    await api.billing.cancelUpcoming(activeOrg.id, u.subscription_id);
    setSuccess(`Invoice cancelled. ${u.description} stays active until ${day(u.issue_on)}, then stops.`);
    await load();
  };

  const reinstateInvoice = async (u: UpcomingInvoice) => {
    if (!activeOrg?.id) return;
    try {
      await api.billing.reinstateUpcoming(activeOrg.id, u.subscription_id);
      setSuccess(`Invoice reinstated. ${u.description} will renew on ${day(u.issue_on)}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reinstate the invoice');
    }
  };

  const canCancel = !!activeRole && CANCEL_ROLES.includes(activeRole);
  const unpaid = invoices.filter((i) => i.status === 'open' && i.amount_due_minor > 0);

  // Links from the payment emails: `?pay=<invoice>` opens the form, `?paid=<invoice>` is
  // where Stripe returns if it ever has to leave the page.
  useEffect(() => {
    if (!activeOrg?.id || loading) return;
    const params = new URLSearchParams(window.location.search);
    const pay = params.get('pay');
    const paid = params.get('paid');
    const change = params.get('change');
    if (change) {
      window.history.replaceState(null, '', window.location.pathname);
      api.billing
        .refreshChange(activeOrg.id, change)
        .then((r) =>
          r.status === 'applied'
            ? setSuccess('Payment received. Your subscription was updated.')
            : r.status === 'refunded'
              ? setError('The subscription changed in the meantime, so the payment was refunded.')
              : undefined,
        )
        .finally(load)
        .catch(() => undefined);
      return;
    }
    if (!pay && !paid) return;
    window.history.replaceState(null, '', window.location.pathname);
    if (pay) {
      const inv = invoices.find((i) => i.id === pay && i.status === 'open');
      if (inv && canCancel) setPaying(inv);
    } else if (paid) {
      api.billing
        .refreshInvoice(activeOrg.id, paid)
        .then((r) => (r.status === 'paid' ? setSuccess(`Invoice ${r.number} is paid. Thank you.`) : undefined))
        .finally(load)
        .catch(() => undefined);
    }
  }, [activeOrg?.id, loading, invoices, canCancel, load]);

  const toggleReminders = async (on: boolean) => {
    if (!activeOrg?.id) return;
    try {
      setAccount(await api.billing.updateAccount(activeOrg.id, { renewal_reminders: on }));
      setSuccess(on ? 'Renewal reminders turned on.' : 'Renewal reminders turned off.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  if (loading) return <Loader label="Loading billing..." />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div>
        <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--fw-bold)' }}>Billing</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4 }}>
          Subscriptions, payment method and invoices for{' '}
          <strong style={{ color: 'var(--text-primary)' }}>{activeOrg?.display_name}</strong>.
        </p>
      </div>

      {!allowed && (
        <Alert tone="info" title="Restricted">
          Only the organisation's owner, admins and billing contacts can see billing.
        </Alert>
      )}
      {error && (
        <Alert tone="danger" title="Error">
          {error}
        </Alert>
      )}
      {success && (
        <Alert tone="success" title="Saved">
          {success}
        </Alert>
      )}

      {entry.unavailable && (
        <Alert tone="info" title="Nothing to add on this plan">
          Your {entry.unavailable === 'hosting' ? 'Hosting' : entry.unavailable} plan is managed by
          CYRN, so it cannot be changed here. Contact us and we will adjust it for you.
        </Alert>
      )}

      {allowed && (
        <>
          <OverdueAlert invoices={unpaid} canPay={canCancel} onPay={setPaying} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 'var(--space-5)' }}>
            <SubscriptionsCard
              orgId={activeOrg?.id ?? ''}
              subscriptions={subscriptions}
              canChange={canCancel}
              openAddFor={entry.add}
              returnTo={entry.returnTo}
              onChanged={async (m) => {
                setSuccess(m);
                await load();
              }}
            />
            <PaymentMethodsCard methods={methods} />
          </div>
          <UpcomingInvoicesCard
            upcoming={upcoming}
            canCancel={canCancel}
            onCancel={setCancelling}
            onReinstate={reinstateInvoice}
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 'var(--space-5)' }}>
            <InvoicesCard orgId={activeOrg?.id ?? ''} invoices={invoices} onOpen={setOpenInvoice} />
            {account && <BillingDetailsCard account={account} onSave={saveAccount} />}
          </div>
          {creditNotes.length > 0 && <CreditNotesCard orgId={activeOrg?.id ?? ''} notes={creditNotes} />}
          {account && <RemindersCard account={account} onToggle={toggleReminders} />}
          <Card padding="var(--space-4)" style={{ background: 'var(--bg-inset)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <ShieldAlert size={18} color="var(--accent)" style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                Card payments are processed by Stripe; CYRN never sees your card number. Business invoices are
                delivered through the French e-invoicing network, and individuals receive theirs by email.
              </div>
            </div>
          </Card>
          {activeOrg?.id && (
            <PayDialog
              orgId={activeOrg.id}
              invoice={paying}
              onClose={() => setPaying(null)}
              onPaid={async () => {
                setSuccess('Payment received. Thank you.');
                await load();
              }}
            />
          )}
          <CancelDialog invoice={cancelling} onClose={() => setCancelling(null)} onConfirm={cancelInvoice} />
          {activeOrg?.id && (
            <InvoiceDialog orgId={activeOrg.id} invoiceId={openInvoice} onClose={() => setOpenInvoice(null)} />
          )}
        </>
      )}
    </div>
  );
}
